import React, { useEffect } from 'react';
import {
  Button,
  Drawer,
  Form,
  Input,
  InputNumber,
  message,
  Select,
  Upload,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import {
  CREATE_ERROR,
  EDIT_ERROR,
  REQUEST_LOADING,
  SUCCESS_CREATE,
  SUCCESS_UPDATE,
} from 'constants/alerts';
import { StoriesService } from 'services/StoriesService';
import { requiredRule } from 'utils/forms/validationRules';
import { normFile } from 'utils/files';
import { CONTENT_SPAN, LABEL_SPAN } from 'constants/table';

function StoriesForm({
  isActive,
  initialValues,
  storyGroups,
  onClose,
  onSuccess,
}) {
  const [form] = Form.useForm();
  const isEditing = initialValues !== null;

  useEffect(() => {
    if (initialValues) {
      const {
        title,
        title_kz,
        background_color,
        description,
        description_kz,
        image,
        image_kz,
        position,
        story_group_id,
        svg,
        text,
        text_color,
        text_kz,
        link,
      } = initialValues;

      form.setFieldsValue({
        title,
        title_kz,
        description,
        description_kz,
        image: image ? [{ name: image }] : undefined,
        image_kz: image_kz ? [{ name: image_kz }] : undefined,
        text,
        text_kz,
        link,
        text_color,
        background_color,
        position,
        story_group_id,
        svg: svg ? [{ name: svg }] : undefined,
      });
    } else {
      form.resetFields();
    }
  }, [initialValues]);

  const handleSubmit = (values) => {
    const formData = new FormData();

    Object.entries(values).forEach(([key, value]) => {
      if (!value) {
        formData.append(key, '');
        return;
      }

      if (key === 'image' || key === 'image_kz' || key === 'svg') {
        if (value[0]?.originFileObj) {
          formData.append(key, value[0]?.originFileObj);
          return;
        }

        if (value[0]) {
          formData.append(key, value[0].name);
          return;
        }

        formData.append(key, '');
        return;
      }

      formData.append(key, value);
    });

    if (isEditing) {
      handleUpdate(formData, initialValues.id);
    } else {
      handleCreate(formData);
    }
  };

  const handleCreate = async (data) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isCreated = await StoriesService.createStories(data);

      if (isCreated) {
        handleConfirm();
        message.success(SUCCESS_CREATE);
      }
    } catch (err) {
      message.error(CREATE_ERROR);
    } finally {
      hide();
    }
  };

  const handleUpdate = async (data, id) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isUpdated = await StoriesService.updateStories(data, id);

      if (isUpdated) {
        handleConfirm();
        message.success(SUCCESS_UPDATE);
      }
    } catch (err) {
      message.error(EDIT_ERROR);
    } finally {
      hide();
    }
  };

  const handleConfirm = () => {
    onSuccess();
    onClose();
    form.resetFields();
  };

  return (
    <Drawer
      title={isEditing ? 'Редактирование все истории' : 'Создание все истории'}
      width="50%"
      onClose={onClose}
      visible={isActive}
      getContainer={false}
    >
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="horizontal"
        labelCol={{
          span: LABEL_SPAN,
        }}
        wrapperCol={{ span: CONTENT_SPAN }}
        labelAlign="right"
        initialValues={{
          text_color: '#000000',
          background_color: '#000000',
        }}
      >
        <Form.Item name="title" label="Заголовок">
          <Input placeholder="Введите заголовок" allowClear />
        </Form.Item>

        <Form.Item name="title_kz" label="Заголовок (каз.)">
          <Input placeholder="Введите заголовок (каз.)" allowClear />
        </Form.Item>

        <Form.Item name="description" label="Описание">
          <Input placeholder="Введите описание" allowClear />
        </Form.Item>

        <Form.Item name="description_kz" label="Описание (каз.)">
          <Input placeholder="Введите описание (каз.)" allowClear />
        </Form.Item>

        <Form.Item name="text" label="Текст">
          <Input placeholder="Введите текст" allowClear />
        </Form.Item>

        <Form.Item name="text_kz" label="Текст (каз.)">
          <Input placeholder="Введите текст (каз.)" allowClear />
        </Form.Item>

        <Form.Item name="link" label="Ссылка">
          <Input placeholder="Введите cсылку" allowClear addonBefore="href" />
        </Form.Item>

        <Form.Item name="text_color" label="Цвет текста">
          <Input
            type="color"
            style={{ width: '50%' }}
            placeholder="Введите цвет текста"
          />
        </Form.Item>

        <Form.Item name="background_color" label="Фоновый цвет">
          <Input
            type="color"
            style={{ width: '50%' }}
            placeholder="Введите фоновый цвет"
          />
        </Form.Item>

        <Form.Item
          name="image"
          label="Медиа"
          valuePropName="fileList"
          getValueFromEvent={normFile}
        >
          <Upload accept=".png" maxCount={1} beforeUpload={() => false}>
            <Button icon={<UploadOutlined />}>Загрузить</Button>
          </Upload>
        </Form.Item>

        <Form.Item
          name="image_kz"
          label="Медиа (каз.)"
          valuePropName="fileList"
          getValueFromEvent={normFile}
        >
          <Upload accept=".png" maxCount={1} beforeUpload={() => false}>
            <Button icon={<UploadOutlined />}>Загрузить</Button>
          </Upload>
        </Form.Item>

        <Form.Item
          name="svg"
          label="SVG файл"
          valuePropName="fileList"
          getValueFromEvent={normFile}
        >
          <Upload accept=".svg" maxCount={1} beforeUpload={() => false}>
            <Button icon={<UploadOutlined />}>Загрузить</Button>
          </Upload>
        </Form.Item>

        <Form.Item
          name="story_group_id"
          label="Группа истории"
          rules={[requiredRule]}
        >
          <Select allowClear placeholder="Группа истории">
            {storyGroups.map(({ id, title }) => (
              <Select.Option key={id} value={id}>
                {id} - {title}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="position" label="Порядок" rules={[requiredRule]}>
          <InputNumber
            controls={false}
            placeholder="Введите порядок"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item wrapperCol={{ offset: CONTENT_SPAN }}>
          <Button type="primary" htmlType="submit">
            Сохранить
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}

export { StoriesForm };
